import { useCallback, useEffect, useRef, useState } from 'react';

const POLLING_INTERVAL = 5000; // 5 seconds
const IDLE_POLLING_INTERVAL = 30000; // 30 seconds when no active tasks

interface UsePollingOptions {
    /** Function to execute on each poll */
    onPoll: () => void | Promise<void>;
    /** Function to check if polling should be active */
    shouldPollCondition: () => boolean;
    /** Interval in milliseconds when actively polling */
    activeInterval?: number;
    /** Interval in milliseconds when idle polling */
    idleInterval?: number;
    /** Data dependency to check initial state */
    data?: any;
    /** Delay before stopping polling when condition becomes false */
    stopDelay?: number;
}

interface UsePollingReturn {
    /** Whether polling is currently active */
    isPolling: boolean;
    /** Start polling */
    startPolling: () => void;
    /** Stop polling */
    stopPolling: () => void;
    /** Toggle polling state */
    togglePolling: () => void;
}

export const usePolling = ({
    onPoll,
    shouldPollCondition,
    activeInterval = POLLING_INTERVAL,
    idleInterval = IDLE_POLLING_INTERVAL,
    data,
    stopDelay = 1000,
}: UsePollingOptions): UsePollingReturn => {
    const [isPolling, setIsPolling] = useState(false);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const stopTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Clear existing polling interval
    const clearPollingInterval = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    }, []);

    // Clear stop timeout
    const clearStopTimeout = useCallback(() => {
        if (stopTimeoutRef.current) {
            clearTimeout(stopTimeoutRef.current);
            stopTimeoutRef.current = null;
        }
    }, []);

    // Start polling with appropriate interval
    const startPollingWithInterval = useCallback(
        (interval: number) => {
            clearPollingInterval();
            const poll = () => onPoll();
            intervalRef.current = setInterval(poll, interval);
        },
        [clearPollingInterval, onPoll],
    );

    // Public API functions
    const startPolling = useCallback(() => {
        setIsPolling(true);
        clearStopTimeout();
    }, [clearStopTimeout]);

    const stopPolling = useCallback(() => {
        setIsPolling(false);
        clearPollingInterval();
        clearStopTimeout();
    }, [clearPollingInterval, clearStopTimeout]);

    const togglePolling = useCallback(() => {
        if (isPolling) {
            stopPolling();
        } else {
            startPolling();
        }
    }, [isPolling, startPolling, stopPolling]);

    // Check initial state when data loads
    useEffect(() => {
        if (data && shouldPollCondition()) {
            setIsPolling(true);
        }
    }, [data, shouldPollCondition]);

    // Main polling effect
    useEffect(() => {
        if (!isPolling) {
            clearPollingInterval();
            return clearPollingInterval;
        }

        const hasActiveCondition = shouldPollCondition();
        const interval = hasActiveCondition ? activeInterval : idleInterval;

        startPollingWithInterval(interval);

        return clearPollingInterval;
    }, [isPolling, shouldPollCondition, activeInterval, idleInterval, startPollingWithInterval, clearPollingInterval]);

    // Auto-stop polling when condition becomes false
    useEffect(() => {
        if (isPolling && !shouldPollCondition() && data) {
            clearStopTimeout();
            stopTimeoutRef.current = setTimeout(() => {
                if (!shouldPollCondition()) {
                    setIsPolling(false);
                }
            }, stopDelay);
        }
    }, [isPolling, shouldPollCondition, data, stopDelay, clearStopTimeout]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            clearPollingInterval();
            clearStopTimeout();
        };
    }, [clearPollingInterval, clearStopTimeout]);

    return {
        isPolling,
        startPolling,
        stopPolling,
        togglePolling,
    };
};
